import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { IBookingExternalAPILog } from 'libs/data-access/types';

/**
 * Downloads API payload data as a formatted JSON file
 * 
 * This function handles both string and object data types, automatically
 * parsing escaped JSON strings and formatting them for better readability.
 * It creates a temporary download link, triggers the download, and cleans up
 * resources to prevent memory leaks.
 * 
 * @param data - The data to download. Can be:
 *   - A string containing JSON (with or without escaped characters)
 *   - A plain string
 *   - An object that will be converted to JSON
 * @param filename - The name of the file to download (without extension)
 * 
 * @example
 * // Download a JSON object
 * downloadFile({ key: "value" }, "api_response");
 * // Result: api_response.json
 * 
 * @example
 * // Download a JSON string with escaped characters
 * downloadFile('{"key":"value\\nwith\\nnewlines"}', "request_payload");
 * // Result: request_payload.json (formatted with actual line breaks)
 * 
 * @example
 * // Download a plain string
 * downloadFile("This is plain text", "log_data");
 * // Result: log_data.json
 * 
 * @throws {Error} When blob creation or download fails
 */
export const downloadFile = (data: any, filename: string): void => {
    try {
        let formattedData: string;

        // If data is already a string, try to parse it as JSON first
        if (typeof data === 'string') {
            try {
                // Try to parse as JSON to handle escaped characters
                // This removes \n, \t, \r, etc. and converts them to actual formatting
                const parsed = JSON.parse(data);
                formattedData = JSON.stringify(parsed, null, 2);
            } catch {
                // If it's not valid JSON, use the string as is
                // This handles plain text responses
                formattedData = data;
            }
        } else {
            // If data is an object, stringify it with proper formatting
            // null, 2 creates 2-space indentation for readability
            formattedData = JSON.stringify(data, null, 2);
        }

        // Convert the formatted data to a blob with JSON MIME type
        const blob = new Blob([formattedData], {
            type: 'application/json'
        });

        // Create a temporary download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename; // Browser will add .json extension automatically
        link.style.display = 'none'; // Hide the link element

        // Trigger the download by programmatically clicking the link
        document.body.appendChild(link);
        link.click();

        // Cleanup: Remove the link and revoke the blob URL to prevent memory leaks
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading file:', error);
    }
};

/**
 * Formats data for inclusion in ZIP file
 * Same logic as downloadFile but returns the formatted string instead of downloading
 */
const formatDataForZip = (data: any): string => {
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return data;
        }
    }
    return JSON.stringify(data, null, 2);
};

/**
 * Creates and downloads a ZIP file containing all API logs for a provider
 * organized by API name in folder structure
 * 
 * @param logs - Array of API logs to include in the ZIP
 * @param providerName - Name of the provider (used in ZIP filename)
 * @param traceId - Trace ID (used in ZIP filename)
 * @param fetchMediaData - Function to fetch media data by ID
 * @param onProgress - Optional callback to report progress (current, total)
 * 
 * @example
 * await createAndDownloadZip(
 *   logs,
 *   'AkbarTravels',
 *   'trace-123',
 *   (id) => getMediaData(`?id=${id}`),
 *   (current, total) => console.log(`${current}/${total}`)
 * );
 */
export const createAndDownloadZip = async (
    logs: IBookingExternalAPILog[],
    providerName: string,
    traceId: string,
    fetchMediaData: (mediaId: number) => Promise<any>,
    onProgress?: (current: number, total: number) => void
): Promise<void> => {
    try {
        const zip = new JSZip();

        // Group logs by apiName
        const logsByApiName: { [apiName: string]: IBookingExternalAPILog[] } = {};
        logs.forEach(log => {
            if (!logsByApiName[log.apiName]) {
                logsByApiName[log.apiName] = [];
            }
            logsByApiName[log.apiName].push(log);
        });

        // Calculate total files to download
        let totalFiles = 0;
        logs.forEach(log => {
            if (log.requestPayloadMediaId) totalFiles++;
            if (log.responsePayloadMediaId) totalFiles++;
        });

        let currentFile = 0;

        // Process each API name group
        for (const [apiName, apiLogs] of Object.entries(logsByApiName)) {
            // Create folder for this API name
            const apiFolder = zip.folder(apiName);

            if (!apiFolder) {
                console.error(`Failed to create folder for ${apiName}`);
                continue;
            }

            // Add files for each log in this API name group
            for (const log of apiLogs) {
                // Add request payload if exists
                if (log.requestPayloadMediaId) {
                    try {
                        currentFile++;
                        onProgress?.(currentFile, totalFiles);

                        const response = await fetchMediaData(log.requestPayloadMediaId);
                        if (response.isSuccess) {
                            const formattedData = formatDataForZip(response.data);
                            const fileName = `${log.apiName}_request_${log.traceId}_${log.id}.${log.apiType}`;
                            apiFolder.file(fileName, formattedData);
                        }
                    } catch (error) {
                        console.error(`Error fetching request payload for log ${log.id}:`, error);
                    }
                }

                // Add response payload if exists
                if (log.responsePayloadMediaId) {
                    try {
                        currentFile++;
                        onProgress?.(currentFile, totalFiles);

                        const response = await fetchMediaData(log.responsePayloadMediaId);
                        if (response.isSuccess) {
                            const formattedData = formatDataForZip(response.data);
                            const fileName = `${log.apiName}_response_${log.traceId}_${log.id}.${log.apiType}`;
                            apiFolder.file(fileName, formattedData);
                        }
                    } catch (error) {
                        console.error(`Error fetching response payload for log ${log.id}:`, error);
                    }
                }
            }
        }

        // Generate ZIP file
        const zipBlob = await zip.generateAsync({ type: 'blob' });

        // Download ZIP file
        const zipFileName = `${providerName}_API_Logs_${traceId}.zip`;
        saveAs(zipBlob, zipFileName);
    } catch (error) {
        console.error('Error creating ZIP file:', error);
        throw error;
    }
};
